<template>
  <Dialog v-model="dialogVisible" :title="isNeedArea ? '补全辖区' : '修改辖区'" width="560px">
    <el-form ref="formRef" v-loading="formLoading" :model="formData" :rules="formRules" label-width="100px">
      <el-form-item label="代理">
        <span>{{ row?.nickname || row?.userId }}</span>
      </el-form-item>
      <el-form-item label="代理类型" prop="agentType">
        <el-select v-model="formData.agentType" class="w-1/1" @change="onAgentTypeChange">
          <el-option
            v-for="o in AREA_AGENT_TYPE"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="辖区" prop="areaIds">
        <el-cascader
          v-model="areaPathList"
          class="w-1/1"
          :options="cascaderOptions"
          :props="cascaderProps"
          clearable
          filterable
          :disabled="!formData.agentType"
          placeholder="可多选同级行政区"
          @change="onAreaChange"
        />
      </el-form-item>
      <el-alert
        v-if="isNeedArea"
        type="info"
        :closable="false"
        title="补全后按「是否需审核」进入待审或直接正常。"
      />
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submit">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { getAreaTree } from '@/api/system/area'
import * as UserApi from '@/api/mall/trade/areaAgent/user'
import { AREA_AGENT_TYPE } from '../constants'

defineOptions({ name: 'AreaAgentUserAreaForm' })

const emit = defineEmits(['success'])
const message = useMessage()
const dialogVisible = ref(false)
const formLoading = ref(false)
const row = ref<UserApi.AreaAgentUserVO>()
const areaTree = ref<any[]>([])
const areaPathList = ref<number[][]>([])
const formRef = ref()
const formData = ref<{ agentType?: number; areaIds: number[] }>({ areaIds: [] })
const formRules = reactive({
  agentType: [{ required: true, message: '请选择代理类型', trigger: 'change' }],
  areaIds: [{ required: true, type: 'array', min: 1, message: '请选择辖区', trigger: 'change' }]
})

const isNeedArea = computed(() => row.value?.status === 5)

const cascaderProps = {
  value: 'id',
  label: 'name',
  children: 'children',
  multiple: true,
  checkStrictly: true,
  emitPath: true
}

const cascaderOptions = computed(() => {
  const depth = formData.value.agentType === 1 ? 1 : formData.value.agentType === 2 ? 2 : 3
  return clipTree(areaTree.value, depth)
})

const clipTree = (nodes: any[], depth: number, level = 1): any[] => {
  if (!nodes?.length || depth < 1) return []
  return nodes.map((n) => {
    const item: any = { id: n.id, name: n.name }
    if (level < depth && n.children?.length) {
      item.children = clipTree(n.children, depth, level + 1)
    }
    return item
  })
}

const findPath = (nodes: any[], targetId: number, path: number[] = []): number[] | null => {
  for (const n of nodes || []) {
    const next = [...path, n.id]
    if (n.id === targetId) return next
    const child = findPath(n.children || [], targetId, next)
    if (child) return child
  }
  return null
}

const onAgentTypeChange = () => {
  areaPathList.value = []
  formData.value.areaIds = []
}

const onAreaChange = (val: number[][] | null) => {
  if (!val?.length) {
    formData.value.areaIds = []
    return
  }
  formData.value.areaIds = [...new Set(val.map((p) => p[p.length - 1]).filter(Boolean))]
}

const open = async (r: UserApi.AreaAgentUserVO) => {
  row.value = r
  if (!areaTree.value.length) {
    areaTree.value = (await getAreaTree()) || []
  }
  formData.value = {
    agentType: r.agentType || 1,
    areaIds: [...(r.areaIdList || [])]
  }
  areaPathList.value = (r.areaIdList || [])
    .map((id) => findPath(areaTree.value, id) || [id])
    .filter(Boolean) as number[][]
  dialogVisible.value = true
}

const submit = async () => {
  await formRef.value?.validate()
  formLoading.value = true
  try {
    await UserApi.checkAreaAgentAreaConflict({
      excludeId: row.value!.id,
      agentType: formData.value.agentType!,
      areaIds: formData.value.areaIds
    })
    await UserApi.completeAreaAgentUserArea({
      id: row.value!.id!,
      agentType: formData.value.agentType!,
      areaIds: formData.value.areaIds
    })
    message.success('保存成功')
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

defineExpose({ open })
</script>
